import React, {useEffect, useMemo, useRef, useState} from "react";
import {flushSync} from "react-dom";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {ConfirmDialog} from "primereact/confirmdialog";
import {DataTable} from "primereact/datatable";
import {Menu} from "primereact/menu";
import {Message} from "primereact/message";
import WorkspaceInvitation from "../../../models/workspace-invitation/workspace-invitation";
import useWorkspaceMembership from "../../../models/workspace-membership/use-workspace-membership";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {dateToPersian} from "../../../system/utils";
import {usePermission} from "../../../system/usePermission";
import {useRealtime} from "../../../system/realtime/useRealtime";
import {useTranslate} from "../../../system/useTranslate";
import {getWorkspaceRole} from "../../../system/workspace-permissions";
import Can from "../../../system/can";
import InviteMemberDialog from "../../components/invite-member-dialog";
import PageHeaderButton from "../../components/page-header-button";
import {buildWorkspaceMemberRows} from "../workspace-members-view";
import {
    isInactiveWorkspaceMembership,
    nextWorkspaceMembershipStatus,
} from "../workspace-member-actions";

const entityId = (entity) => entity?.documentId || entity?.id;

function ManageMembers() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const permission = usePermission();
    const realtime = useRealtime();
    const membershipApi = useWorkspaceMembership();
    const menuRef = useRef(null);
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const workspace = workspaceMembershipData?.workspace;
    const [inviteDialogVisible, setInviteDialogVisible] = useState(false);
    const [invitations, setInvitations] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [loadingInvitations, setLoadingInvitations] = useState(false);
    const [pendingAction, setPendingAction] = useState("");
    const [actionError, setActionError] = useState("");

    const loadInvitations = async () => {
        if (!workspace?.documentId || !permission.can("workspace.members.manage")) return;
        setLoadingInvitations(true);
        try {
            const response = await new WorkspaceInvitation().getAll({
                filters: {invitationStatus: {$in: ["pending", "accepted"]}},
                sort: ["createdAt:desc"],
                populate: {
                    invitee: {populate: ["profileImage"]},
                    inviter: {populate: {member: {populate: ["profileImage"]}}},
                },
                pagination: {pageSize: 200, withCount: false},
            });
            setInvitations(response?.data || []);
        } catch (requestError) {
            setActionError(translate("workspace-members.error.load-invitations"));
        } finally {
            setLoadingInvitations(false);
        }
    };

    useEffect(() => {
        loadInvitations();
        // Reload only when the active tenant changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspace?.documentId]);

    const rows = useMemo(() => buildWorkspaceMemberRows({
        memberships: workspace?.workspace_memberships || [],
        invitations,
        ownerMembership: workspace?.owner,
    }), [invitations, workspace?.owner, workspace?.workspace_memberships]);
    const selectedIsOwner = selectedRow?.isWorkspaceOwner === true;
    const selectedIsInvitation = selectedRow?.rowType === "invitation";
    const selectedIsActive = !isInactiveWorkspaceMembership(selectedRow);

    const refreshMembers = async () => {
        await Promise.all([
            membershipApi.getWorkspaceMemberships(),
            loadInvitations(),
        ]);
    };

    const toggleMemberStatus = async () => {
        if (!permission.can("workspace.members.manage", true)
            || !selectedRow?.membershipId
            || selectedIsOwner) return;
        setPendingAction("status");
        setActionError("");
        try {
            const updated = await membershipApi.updateWorkspaceMembershipStatus(
                selectedRow.membershipId,
                nextWorkspaceMembershipStatus(selectedRow),
            );
            if (!updated) throw new Error("workspace-membership.error.update-status-failed");
            setSelectedRow(null);
        } catch (requestError) {
            setActionError(translate(
                requestError?.normalizedError?.message
                || requestError?.message
                || "workspace-membership.error.update-status-failed",
            ));
        } finally {
            setPendingAction("");
        }
    };

    const removeSelected = async () => {
        if (!permission.can("workspace.members.manage", true) || selectedIsOwner) return;
        const identifier = selectedIsInvitation
            ? selectedRow?.invitationId
            : selectedRow?.membershipId;
        if (!identifier) return;
        setPendingAction("remove");
        setActionError("");
        try {
            const result = selectedIsInvitation
                ? await new WorkspaceInvitation().deleteItem(identifier)
                : await membershipApi.deleteWorkspaceMemberShip(identifier);
            if (!result) throw new Error("workspace-members.error.remove-failed");
            await refreshMembers();
            setSelectedRow(null);
        } catch (requestError) {
            setActionError(translate(
                requestError?.normalizedError?.message
                || requestError?.message
                || "workspace-members.error.remove-failed",
            ));
        } finally {
            setPendingAction("");
        }
    };

    const menuOptions = [
        ...(!selectedIsInvitation && permission.can("workspace.roles.manage") && !selectedIsOwner ? [{
            label: translate("workspace-members.manage-access"),
            icon: <Icon icon="mi mi:shield_lock" className="text-lg"/>,
            className: "text-xs gap-2",
            command: () => navigate("/settings?tab=access-control"),
        }] : []),
        ...(!selectedIsInvitation ? [{
            label: translate(selectedIsActive ? "disable" : "enable"),
            icon: <Icon icon={`mi mi:${selectedIsActive ? "block" : "check_circle"}`} className="text-lg"/>,
            className: "text-xs gap-2",
            command: toggleMemberStatus,
            disabled: !permission.can("workspace.members.manage") || selectedIsOwner || Boolean(pendingAction),
        }] : []),
        {
            label: translate(selectedIsInvitation ? "workspace-members.cancel-invitation" : "remove-from-workspace"),
            icon: <Icon icon="mi mi:delete" className="text-lg"/>,
            className: "text-xs gap-2 text-red-500",
            command: () => setDeleteDialogVisible(true),
            disabled: !permission.can("workspace.members.manage") || selectedIsOwner || Boolean(pendingAction),
        },
    ];

    const nameTemplate = (row) => {
        const member = row.member || {};
        return (
            <div
                role={row.rowType === "member" ? "button" : undefined}
                tabIndex={row.rowType === "member" ? 0 : undefined}
                className={`p-0 flex align-items-center gap-2 text-right ${row.rowType === "member" ? "cursor-pointer" : "cursor-default"}`}
                onClick={() => row.rowType === "member" && navigate(`/user-profile/${entityId(member)}`)}
                onKeyDown={(event) => {
                    if (row.rowType === "member" && ["Enter", " "].includes(event.key)) {
                        navigate(`/user-profile/${entityId(member)}`);
                    }
                }}
            >
                <Avatar memberData={member} className="text-2xs font-bold"/>
                <span className="flex flex-column gap-1 min-w-0">
                    <span className="text-xs text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {member.fullname || member.username || translate("workspace-members.unknown-member")}
                    </span>
                    <small className="text-2xs text-400 ltr text-right white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {member.phonenumber || "—"}
                    </small>
                </span>
            </div>
        );
    };

    const positionTemplate = (row) => {
        const role = getWorkspaceRole(row.userRoles || []);
        const label = row.member?.jobPosition
            || translate(`workspace-members.role.${role?.role || row.invitationRole || "viewer"}`);
        return (
            <div className="flex align-items-center gap-2 text-xs text-800 white-space-nowrap">
                <Icon icon={role?.role === "owner" || role?.role === "admin" ? "mi mi:manage_accounts" : "mi mi:badge"} className="text-lg text-400"/>
                <span>{label}</span>
            </div>
        );
    };

    const statusTemplate = (row) => {
        const online = row.rowType === "member" && realtime.isMemberOnline(row);
        const inactive = isInactiveWorkspaceMembership(row);
        const status = row.rowType === "invitation"
            ? {label: translate("workspace-members.pending"), color: "bg-orange-400", text: "text-orange-500"}
            : inactive
                ? {label: translate("workspace-members.inactive"), color: "bg-gray-300", text: "text-500"}
                : online
                    ? {label: translate("workspace-members.active"), color: "bg-green-500", text: "text-green-600"}
                    : {label: translate("presence.offline"), color: "bg-gray-300", text: "text-500"};
        return (
            <span className={`inline-flex align-items-center gap-2 text-xs white-space-nowrap ${status.text}`}>
                <span className={`block border-circle p-1 ${status.color}`}/>
                {status.label}
            </span>
        );
    };

    const lastActivityTemplate = (row) => {
        if (row.rowType === "invitation") return "—";
        if (realtime.isMemberOnline(row)) return translate("workspace-members.online-now");
        return row.updatedAt ? dateToPersian(row.updatedAt, "YYYY/MM/DD") : "—";
    };
    const inviterTemplate = (row) => row.inviter?.fullname || translate("workspace-members.direct-join");
    const invitationDateTemplate = (row) => row.invitedAt
        ? dateToPersian(row.invitedAt, "YYYY/MM/DD")
        : "—";
    const actionsTemplate = (row) => row.isWorkspaceOwner ? null : (
        <Button
            text
            rounded
            severity="secondary"
            className="w-2rem h-2rem p-0"
            icon="pi pi-ellipsis-h"
            aria-label={translate("actions")}
            onClick={(event) => {
                event.stopPropagation();
                setActionError("");
                flushSync(() => setSelectedRow(row));
                menuRef.current?.toggle(event);
            }}
        />
    );

    return (
        <div className="flex flex-column p-3 gap-4">
            <PageHeaderButton
                title={translate("manage-members")}
                end={<Can permission="workspace.members.manage"><Button icon={<Icon icon="mi mi:person_add"/>} className="text-xs gap-2" onClick={() => setInviteDialogVisible(true)} label={translate("invite-member")}/></Can>}
            />

            {actionError && <Message severity="error" className="text-xs justify-content-start" text={actionError}/>}

            <section className="surface-card border-1 border-100 border-round-2xl overflow-hidden">
                <DataTable
                    value={rows}
                    loading={loadingInvitations}
                    scrollable
                    tableStyle={{minWidth: "72rem"}}
                    emptyMessage={translate("workspace-members.empty")}
                    className="text-xs"
                    dir="rtl"
                    rowClassName={() => "cursor-default"}
                    pt={{
                        headerRow: {className: "surface-0"},
                        headerCell: {className: "surface-0 text-700 font-normal text-xs text-right py-3 px-3 border-bottom-1 border-100"},
                        bodyRow: {className: "surface-0 hover:surface-50"},
                        bodyCell: {className: "text-xs text-right py-3 px-3 border-bottom-1 border-100"},
                    }}
                >
                    <Column className="text-right" header="#" body={(_, options) => options.rowIndex + 1} style={{width: "3.5rem"}}/>
                    <Column className="text-right" header={translate("name")} body={nameTemplate} sortable sortField="member.fullname" style={{minWidth: "14rem"}}/>
                    <Column className="text-right" header={translate("workspace-members.organizational-position")} body={positionTemplate} sortable sortField="member.jobPosition" style={{minWidth: "11rem"}}/>
                    <Column className="text-right" header={translate("status")} body={statusTemplate} sortable sortField="memberShipStatus" style={{minWidth: "8rem"}}/>
                    <Column
                        className="text-right"
                        header={<span className="flex align-items-center gap-2"><Icon icon="mi mi:info" className="text-base"/>{translate("security.last-activity")}</span>}
                        body={lastActivityTemplate}
                        style={{minWidth: "9rem"}}
                    />
                    <Column className="text-right" header={translate("workspace-members.invited-by")} body={inviterTemplate} style={{minWidth: "10rem"}}/>
                    <Column className="text-right" header={translate("workspace-members.invitation-date")} body={invitationDateTemplate} sortable sortField="invitedAt" style={{minWidth: "8rem"}}/>
                    <Column className="text-right" body={actionsTemplate} style={{width: "3.5rem"}}/>
                </DataTable>
            </section>

            <Menu
                ref={menuRef}
                popup
                model={menuOptions}
                className="rtl w-fit"
                pt={{root: {className: "p-1"}, action: {className: "px-2 py-2 gap-2 text-xs"}}}
            />
            <InviteMemberDialog
                visible={inviteDialogVisible}
                setVisible={setInviteDialogVisible}
                onSubmit={refreshMembers}
            />
            <ConfirmDialog
                className="rtl"
                visible={deleteDialogVisible}
                onHide={() => setDeleteDialogVisible(false)}
                header={translate(selectedIsInvitation ? "workspace-members.cancel-invitation" : "remove-from-workspace")}
                message={translate(selectedIsInvitation
                    ? "workspace-members.cancel-invitation-confirm"
                    : "remove-from-workspace-dialog-message")
                    .replace("{{FULLNAME}}", selectedRow?.member?.fullname || "")}
                icon="pi pi-exclamation-triangle"
                acceptClassName="p-button-danger text-xs"
                rejectClassName="p-button-danger p-button-text text-xs"
                acceptLabel={translate(selectedIsInvitation ? "workspace-members.cancel-invitation" : "remove-from-workspace")}
                rejectLabel={translate("cancel")}
                accept={removeSelected}
                reject={() => setSelectedRow(null)}
            />
        </div>
    );
}

export default ManageMembers;
